import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import Button from '../../components/common/Button';
import { workflowsApi, extractList } from '../../api/facades';

const WorkflowsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: res, isLoading } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => workflowsApi.list({ per_page: 50 }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => workflowsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setDeleteId(null);
      toast.success('Workflow deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description', render: (r: any) => (r.description ? String(r.description).slice(0, 80) + (r.description.length > 80 ? '…' : '') : '—') },
    { key: 'is_active', label: 'Active' },
  ];

  return (
    <div>
      <PageHeader hideTitle subtitle="Manage task workflows." primaryAction={{ label: 'New Workflow', to: '/dashboard/workflows/new' }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          emptyMessage="No workflows."
          onRowClick={(id) => navigate(`/dashboard/workflows/${id}`)}
          onEdit={(id) => navigate(`/dashboard/workflows/${id}/edit`)}
          onDelete={(id) => setDeleteId(Number(id))}
        />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete workflow" message="Are you sure? All tasks in this workflow will be deleted." loading={deleteMu.isPending} />
    </div>
  );
};

export default WorkflowsListPage;
